/* Cart (§8.4): <cart-items> quantity/remove/note updates via /cart/change and /cart/update,
   AJAX add-to-cart for any form[data-type="add-to-cart-form"], section re-rendering by
   [data-cart-region], and the free-delivery line for converted currencies. Used by the drawer
   and the cart page. */
(() => {
  if (window.Theme.cartReady) return;
  window.Theme.cartReady = true;

  const { config } = window.Theme;
  const headers = { 'Content-Type': 'application/json', Accept: 'application/javascript' };

  // Every <cart-items> on the page lists the sections it lives in; re-render all of them together.
  const sectionIds = () => [
    ...new Set(
      [...document.querySelectorAll('cart-items')].flatMap((el) => (el.dataset.sections || '').split(',')).filter(Boolean)
    ),
  ];

  function renderSections(sections) {
    if (!sections) return;
    Object.values(sections).forEach((html) => {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('[data-cart-region]').forEach((fresh) => {
        document
          .querySelectorAll(`[data-cart-region="${fresh.dataset.cartRegion}"]`)
          .forEach((current) => (current.innerHTML = fresh.innerHTML));
      });
    });
    document.querySelectorAll('[data-free-delivery]').forEach(updateFreeDelivery);
  }

  // The threshold is set in the store currency; scale it for the visitor's currency.
  function updateFreeDelivery(el) {
    const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    if (rate === 1) return;
    const threshold = Math.round(Number(el.dataset.threshold) * rate);
    const total = Number(el.dataset.total);
    const remaining = threshold - total;
    const percent = Math.min(100, (total / threshold) * 100);
    el.querySelector('[data-free-msg]').innerHTML =
      remaining > 0 ? el.dataset.template.replace('[amount]', window.Theme.formatMoney(remaining)) : el.dataset.unlocked;
    el.style.setProperty('--free', `${percent.toFixed(1)}%`);
    el.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', String(Math.round(percent)));
  }

  async function post(url, body) {
    const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    const json = await response.json();
    if (!response.ok || json.status) throw new Error(json.description || json.message || config.strings.cartError);
    return json;
  }

  const announce = (detail) => document.dispatchEvent(new CustomEvent('cart:updated', { detail }));

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.onClick = (event) => {
        const step = event.target.closest('[data-qty-step]');
        const remove = event.target.closest('[data-remove]');
        if (step) {
          const input = step.closest('[data-line]').querySelector('[data-line-qty]');
          input.value = Math.max(0, Number(input.value) + Number(step.dataset.qtyStep));
          this.queue(input);
        } else if (remove) {
          event.preventDefault();
          const line = remove.closest('[data-line]');
          this.update(line, 0);
        }
      };
      this.onChange = (event) => {
        if (event.target.matches('[data-line-qty]')) this.queue(event.target);
        if (event.target.matches('[data-cart-note]')) this.saveNote(event.target.value);
      };
      this.saveNote = window.Theme.debounce((note) => post(config.routes.cartUpdate, { note }).catch(() => {}), 300);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
    }

    queue(input) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(input.closest('[data-line]'), Number(input.value)), 350);
    }

    async update(line, quantity) {
      const key = line.dataset.key;
      const hadFocus = line.contains(document.activeElement);
      this.setAttribute('aria-busy', 'true');
      try {
        const cart = await post(config.routes.cartChange, {
          line: Number(line.dataset.line),
          quantity,
          sections: sectionIds().join(','),
          sections_url: window.location.pathname,
        });
        renderSections(cart.sections);
        announce({ count: cart.item_count, source: 'change' });
        if (hadFocus) this.restoreFocus(key);
      } catch (error) {
        const message = line.querySelector('[data-line-error]');
        if (message) message.textContent = error.message;
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    // After a re-render, put focus back on the same line, or on the heading if the line is gone.
    restoreFocus(key) {
      const line = [...this.querySelectorAll('[data-key]')].find((el) => el.dataset.key === key);
      const target = line ? line.querySelector('[data-line-qty]') : this.querySelector('h1, h2');
      if (!target) return;
      if (!line) target.setAttribute('tabindex', '-1');
      target.focus();
    }
  }
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

  /* Add to cart: intercept product forms when the cart type is drawer. */
  function showFormError(form, message) {
    let el = form.querySelector('[data-form-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'form-error';
      el.setAttribute('role', 'alert');
      el.dataset.formError = '';
      form.append(el);
    }
    el.textContent = message;
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    const drawer = document.getElementById('CartDrawer');
    if (config.cartType !== 'drawer' || !drawer) return;
    event.preventDefault();

    const submit = event.submitter || form.querySelector('[type="submit"]');
    if (submit && submit.getAttribute('aria-busy') === 'true') return;
    if (submit) submit.setAttribute('aria-busy', 'true');
    showFormError(form, '');

    const data = new FormData(form);
    const item = { id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) };
    const properties = {};
    for (const [name, value] of data) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    if (data.get('selling_plan')) item.selling_plan = Number(data.get('selling_plan'));

    try {
      const response = await post(config.routes.cartAdd, {
        items: [item],
        sections: sectionIds().concat('cart-drawer', 'cart-count').filter((id, i, all) => all.indexOf(id) === i).join(','),
        sections_url: window.location.pathname,
      });
      renderSections(response.sections);
      announce({ source: 'add' });
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { form } }));
      // From quick add, focus returns to the product card when the cart drawer closes.
      const opener = form.closest('#QuickAdd') ? window.Theme.quickAddOpener : submit;
      drawer.open(opener);
    } catch (error) {
      showFormError(form, error.message);
    } finally {
      if (submit) submit.removeAttribute('aria-busy');
    }
  });

  /* Theme editor: selecting the cart drawer section opens it. */
  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.id === 'shopify-section-cart-drawer') document.getElementById('CartDrawer')?.open();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.target.id === 'shopify-section-cart-drawer') document.getElementById('CartDrawer')?.close();
  });

  document.querySelectorAll('[data-free-delivery]').forEach(updateFreeDelivery);
  Object.assign(window.Theme, { renderCartSections: renderSections });
})();
